import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Zap, UserRound, Wallet } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, BRAND } from "../data/siteData";

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow,border-color] duration-300 ${
                scrolled
                    ? "border-b border-white/10 bg-[#0B0F19]/85 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-[72px] sm:px-6 lg:px-8">
                <Link to="/" aria-label="Eway Fiber Net home" data-testid="nav-logo-link">
                    <Logo compact />
                </Link>

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.id}
                            to={link.to}
                            data-testid={`nav-link-${link.id}`}
                            className={({ isActive }) =>
                                `whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold transition-colors duration-200 ${
                                    isActive
                                        ? "bg-white/10 text-white"
                                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2.5">
                    <a
                        href={BRAND.phones[0].tel}
                        data-testid="nav-call-button"
                        className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:border-[#00F0FF]/60 hover:text-[#00F0FF] xl:flex"
                    >
                        <Phone className="h-4 w-4" />
                        {BRAND.phones[0].label}
                    </a>
                    <a
                        href={BRAND.links.customerPortal}
                        data-testid="nav-customer-login-button"
                        className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-white/25 bg-white px-4 py-2 text-sm font-extrabold tracking-wide text-[#C7151C] transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-slate-200 lg:flex"
                    >
                        <UserRound className="h-4 w-4" />
                        CUSTOMER LOGIN
                    </a>
                    <a
                        href={BRAND.links.payNow}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-pay-now-button"
                        className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-[#00F0FF] px-4 py-2 text-sm font-extrabold tracking-wide text-[#0B0F19] shadow-[0_0_24px_rgba(0,240,255,0.35)] transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-[#5df6ff] lg:flex"
                    >
                        <Wallet className="h-4 w-4" />
                        PAY NOW
                    </a>
                    <Link
                        to="/get-connected"
                        data-testid="nav-get-connection-button"
                        className="group hidden items-center gap-2 whitespace-nowrap rounded-full bg-[#E61C24] px-5 py-2.5 text-sm font-bold text-white shadow-[0_0_24px_rgba(230,28,36,0.45)] transition-[background-color,transform,box-shadow] duration-200 hover:scale-105 hover:bg-[#C7151C] sm:flex"
                    >
                        <Zap className="h-4 w-4 transition-transform duration-200 group-hover:rotate-12" />
                        Get New Connection
                    </Link>
                    <button
                        type="button"
                        aria-label="Open menu"
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen((v) => !v)}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        key="mobile-nav"
                        data-testid="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-b border-white/10 bg-[#0B0F19]/95 backdrop-blur-xl lg:hidden"
                        aria-label="Mobile"
                    >
                        <div className="space-y-1 px-4 py-4">
                            {NAV_LINKS.map((link) => (
                                <NavLink
                                    key={link.id}
                                    to={link.to}
                                    data-testid={`mobile-nav-link-${link.id}`}
                                    className={({ isActive }) =>
                                        `block rounded-xl px-4 py-3 text-base font-semibold transition-colors duration-200 ${
                                            isActive
                                                ? "bg-[#E61C24]/15 text-[#ff5a60]"
                                                : "text-slate-200 hover:bg-white/5 hover:text-white"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            ))}
                            <div className="mt-2 grid grid-cols-2 gap-2.5">
                                <a
                                    href={BRAND.links.customerPortal}
                                    data-testid="mobile-customer-login-button"
                                    className="flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-white px-4 py-3.5 text-sm font-extrabold tracking-wide text-[#C7151C] transition-colors duration-200 hover:bg-slate-200"
                                >
                                    <UserRound className="h-4 w-4" />
                                    CUSTOMER LOGIN
                                </a>
                                <a
                                    href={BRAND.links.payNow}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="mobile-pay-now-button"
                                    className="flex items-center justify-center gap-2 rounded-xl bg-[#00F0FF] px-4 py-3.5 text-sm font-extrabold tracking-wide text-[#0B0F19] transition-colors duration-200 hover:bg-[#5df6ff]"
                                >
                                    <Wallet className="h-4 w-4" />
                                    PAY NOW
                                </a>
                            </div>
                            <Link
                                to="/get-connected"
                                data-testid="mobile-get-connection-button"
                                className="mt-2.5 flex items-center justify-center gap-2 rounded-xl bg-[#E61C24] px-4 py-3.5 text-base font-bold text-white transition-colors duration-200 hover:bg-[#C7151C]"
                            >
                                <Zap className="h-4 w-4" />
                                Get New Connection
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
